import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BusinessIntelDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
        fetchLeaderboard();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/analytics/dashboard/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDashboardData(data);
        } catch (err) {
            console.error('Error fetching dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeaderboard = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/analytics/leaderboard/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setLeaderboard(data);
        } catch (err) {
            console.error('Error fetching leaderboard:', err);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Business Intelligence</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Analyze performance, track metrics, and gain insights</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Active Cases</p>
                            <p className="text-3xl font-bold mt-1">{dashboardData?.overview?.total_cases || 0}</p>
                        </div>
                        <div className="p-3 bg-blue-400/30 rounded-lg">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-blue-100 text-sm">
                        <span className="text-green-300">+{dashboardData?.overview?.new_cases_this_month || 0}</span>
                        <span className="ml-1">this month</span>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Active Clients</p>
                            <p className="text-3xl font-bold mt-1">{dashboardData?.overview?.total_clients || 0}</p>
                        </div>
                        <div className="p-3 bg-green-400/30 rounded-lg">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Tasks Completed</p>
                            <p className="text-3xl font-bold mt-1">{dashboardData?.productivity?.tasks_completed || 0}</p>
                        </div>
                        <div className="p-3 bg-purple-400/30 rounded-lg">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-purple-100 text-sm">
                        <span className="text-red-300">{dashboardData?.productivity?.tasks_overdue || 0}</span>
                        <span className="ml-1">overdue</span>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">Deadlines</p>
                            <p className="text-3xl font-bold mt-1">{dashboardData?.deadlines?.upcoming_7_days || 0}</p>
                        </div>
                        <div className="p-3 bg-orange-400/30 rounded-lg">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-orange-100 text-sm">
                        <span className="text-red-300">{dashboardData?.deadlines?.overdue || 0}</span>
                        <span className="ml-1">overdue</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* Case Distribution */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cases by Status</h2>
                    <div className="space-y-4">
                        {dashboardData?.cases?.status_distribution &&
                            Object.entries(dashboardData.cases.status_distribution).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between">
                                    <span className="text-gray-600 dark:text-gray-300 capitalize">{status.toLowerCase().replace('_', ' ')}</span>
                                    <div className="flex items-center">
                                        <div className="w-32 h-2 bg-gray-200 rounded-full mr-3">
                                            <div
                                                className="h-2 bg-blue-500 rounded-full"
                                                style={{ width: `${Math.min((count / (dashboardData?.overview?.total_cases || 1)) * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                        <span className="font-medium text-gray-900 dark:text-white w-8 text-right">{count}</span>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>

                {/* Activity Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">This Month's Activity</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <p className="text-2xl font-bold text-blue-600">
                                {dashboardData?.productivity?.documents_generated || 0}
                            </p>
                            <p className="text-sm text-gray-600">Documents</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                            <p className="text-2xl font-bold text-green-600">
                                {dashboardData?.productivity?.signatures_completed || 0}
                            </p>
                            <p className="text-sm text-gray-600">Signatures</p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                            <p className="text-2xl font-bold text-purple-600">
                                {dashboardData?.overview?.new_cases_this_month || 0}
                            </p>
                            <p className="text-sm text-gray-600">New Cases</p>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <p className="text-2xl font-bold text-orange-600">
                                {dashboardData?.overview?.cases_closed_this_month || 0}
                            </p>
                            <p className="text-sm text-gray-600">Cases Closed</p>
                        </div>
                    </div>
                </div>

                {/* Leaderboard */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Performers</h2>
                    <div className="space-y-3">
                        {leaderboard.length > 0 ? (
                            leaderboard.slice(0, 5).map((entry, index) => (
                                <div key={entry.id} className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                                index === 1 ? 'bg-gray-100 text-gray-700' :
                                                    index === 2 ? 'bg-orange-100 text-orange-700' :
                                                        'bg-gray-50 text-gray-600'
                                            }`}>
                                            {index + 1}
                                        </span>
                                        <span className="ml-3 text-gray-900 dark:text-white">{entry.name}</span>
                                    </div>
                                    <span className="font-medium text-gray-600 dark:text-gray-300">{entry.score} pts</span>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 text-center py-4">No data available</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Trends Chart Placeholder */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Monthly Case Trends</h2>
                <div className="h-64 flex items-end justify-around gap-2 px-4">
                    {dashboardData?.trends?.monthly_cases?.map((month, index) => (
                        <div key={index} className="flex flex-col items-center flex-1">
                            <div
                                className="w-full bg-blue-500 rounded-t-lg transition-all hover:bg-blue-600"
                                style={{
                                    height: `${Math.max((month.cases / Math.max(...dashboardData.trends.monthly_cases.map(m => m.cases), 1)) * 200, 10)}px`
                                }}
                            ></div>
                            <span className="text-xs text-gray-500 mt-2">{month.month}</span>
                            <span className="text-xs font-medium text-gray-700">{month.cases}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                <Link
                    to="/analytics/reports"
                    className="flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Reports
                </Link>
                <Link
                    to="/analytics/productivity"
                    className="flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    Productivity
                </Link>
                <Link
                    to="/analytics/revenue"
                    className="flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Revenue
                </Link>
                <Link
                    to="/analytics/settings"
                    className="flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                </Link>
            </div>
        </div>
    );
};

export default BusinessIntelDashboard;
